import React, { useState, useEffect } from 'react';
import { SchemaMarkup } from '../components/SEO/SchemaMarkup';
import { ENTITY } from '../data/entityData';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ChevronRight, Phone, Star } from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import GeometricPattern from '../components/GeometricPattern';
import CrescentMoon from '../components/CrescentMoon';
import { PACKAGES as mockPackages, COMPANY } from '../data/mock';
import { brandConfig } from '../brandConfig';
import { sacredRise, stagger } from '../lib/animations';

const FILTERS = ['All', 'Umrah'];

export default function Packages() {
  const [filter, setFilter] = useState('All');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe;
    // Check if the global listener from index.html is available
    if (window.listenToPackages) {
      unsubscribe = window.listenToPackages((data) => {
        if (data.length === 0 && window.seedPackages && !window.hasSeededPackages) {
          window.hasSeededPackages = true;
          window.seedPackages(mockPackages);
        }
        setPackages(data);
        setLoading(false);
      });
    } else {
      setPackages(mockPackages); // fallback if script not loaded
      setLoading(false);
    }
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const filtered = filter === 'All' ? packages : packages.filter(p => p.type === filter);

  const handleSeed = () => {
    if (window.seedPackages) {
      window.seedPackages(mockPackages);
    }
  };

  return (
    <PageWrapper>
      <SchemaMarkup
        pageTitle={`Umrah & Hajj Packages | ${brandConfig.companyName}`}
        pageDescription="Browse our affordable and VIP Umrah packages starting from ETB 85,000. Family bundles, Ramadan specials, and premium 5-star accommodations available."
        pageUrl="/packages"
        faqItems={ENTITY.pageFAQ?.packages}
      />
      {/* HERO */}
      <section style={{ position: 'relative', minHeight: '55vh', overflow: 'hidden', background: '#0A1628', display: 'flex', alignItems: 'center' }}>
        <img src="https://images.unsplash.com/photo-1592326871020-04f58c1a52f3?w=1920&h=600&fit=crop&q=80"
          alt="Packages" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,22,40,0.85)' }} />
        <GeometricPattern color="#C9A84C" opacity={0.05} size={100} />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 'var(--container)', margin: '0 auto', padding: '120px var(--gutter) 60px', width: '100%' }}>
          <div className="arabic-text" style={{ fontSize: 18, color: '#C9A84C' }}>{COMPANY.bismillah}</div>
          <h1 style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#FFFFFF', fontSize: 'var(--h1)', marginTop: 16, lineHeight: 1.1 }}>
            Our Packages.<br /><span style={{ color: '#C9A84C' }}>Sacred Journeys.</span>
          </h1>
          <p style={{ fontFamily: 'Inter', color: '#FBF8F0', fontSize: 'var(--body-lg)', maxWidth: 560, marginTop: 16 }}>
            Carefully crafted Umrah packages from Addis Ababa. Visa, ticket, hotel, transport — all included.
          </p>
        </div>
      </section>

      {/* FILTERS */}
      <section style={{ background: '#FBF8F0', padding: '60px 0 20px' }}>
        <div style={{ maxWidth: 'var(--container)', margin: '0 auto', padding: '0 var(--gutter)', display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          {FILTERS.map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              fontFamily: 'Inter', fontWeight: 600, fontSize: 12, padding: '10px 28px',
              letterSpacing: '0.15em', textTransform: 'uppercase', cursor: 'pointer',
              background: filter === f ? '#1B6B3A' : 'transparent',
              color: filter === f ? '#FBF8F0' : '#0A1628',
              border: `2px solid ${filter === f ? '#1B6B3A' : 'rgba(201,168,76,0.4)'}`,
              transition: 'all 0.25s',
            }}>{f}</button>
          ))}
        </div>
      </section>

      {packages.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ fontFamily: 'Inter', color: '#0A1628', marginBottom: '16px' }}>No packages found in Firestore database.</p>
          <button onClick={handleSeed} style={{ padding: '12px 24px', background: '#1B6B3A', color: '#fff', cursor: 'pointer', border: 'none', fontFamily: 'Inter', fontWeight: 600, borderRadius: '4px' }}>
            Seed Initial Packages to Database
          </button>
        </div>
      )}

      {/* GRID */}
      <section style={{ background: '#FBF8F0', padding: '40px 0 var(--section)' }}>
        <motion.div initial="hidden" animate="visible" variants={stagger(0.1)}
          className="packages-grid"
          style={{
            maxWidth: 'var(--container)', margin: '0 auto', padding: '0 var(--gutter)',
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 28,
          }}>
          {filtered.map((p) => (
            <motion.div key={p.id} variants={sacredRise} className="pkg-card" style={{
              background: '#FFFFFF', border: '1px solid rgba(201,168,76,0.2)',
              borderTop: '4px solid #1B6B3A', overflow: 'hidden', transition: 'all 0.3s',
            }}>
              <div style={{ position: 'relative', overflow: 'hidden', aspectRatio: '16/9' }}>
                <img src={p.image} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.6s' }} className="pkg-img" />
                {p.featured && (
                  <div style={{
                    position: 'absolute', top: 16, left: 0, background: '#C9A84C',
                    color: '#0A1628', padding: '6px 14px', fontFamily: 'Inter', fontWeight: 700,
                    fontSize: 10, letterSpacing: '0.15em',
                  }}>★ POPULAR</div>
                )}
              </div>
              <div style={{ padding: '28px 24px' }}>
                <div style={{
                  display: 'inline-block', marginBottom: 14, background: 'rgba(27,107,58,0.1)',
                  border: '1px solid rgba(27,107,58,0.3)', padding: '4px 14px',
                  fontFamily: 'Inter', fontWeight: 600, color: '#1B6B3A',
                  fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase',
                }}>{p.type} · {p.duration}</div>
                <div style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#0A1628', fontSize: 22 }}>{p.name}</div>
                <div className="arabic-text" style={{ fontStyle: 'italic', color: '#C9A84C', fontSize: 15, marginTop: 4 }}>{p.arabic}</div>
                <div style={{ fontFamily: 'Inter', color: '#0A1628', fontSize: 14, lineHeight: 1.7, marginTop: 12 }}>{p.description}</div>
                <div style={{ marginTop: 16 }}>
                  {p.includes.map(inc => (
                    <div key={inc} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0A1628', fontSize: 13, marginBottom: 6 }}>
                      <Check size={14} color="#1B6B3A" /> {inc}
                    </div>
                  ))}
                </div>
                <div style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#1B6B3A', fontSize: 22, marginTop: 20 }}>{p.price}</div>
                <Link to="/contact" style={{ textDecoration: 'none' }}>
                  <button style={{
                    marginTop: 16, width: '100%', padding: 14, background: '#1B6B3A',
                    color: '#FBF8F0', border: 'none', fontFamily: 'Inter', fontWeight: 600,
                    fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer',
                    transition: 'background 0.2s',
                  }} onMouseEnter={(e) => (e.target.style.background = '#0A1628')}
                     onMouseLeave={(e) => (e.target.style.background = '#1B6B3A')}>
                    Book This Package
                  </button>
                </Link>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <style>{`
        .pkg-card:hover .pkg-img { transform: scale(1.08); }
        .pkg-card:hover { box-shadow: 0 20px 50px rgba(10,22,40,0.12); transform: translateY(-4px); }
        @media (max-width: 1023px) { .packages-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 640px) { .packages-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </PageWrapper>
  );
}
